/**
 * Feature flag client — the frontend half of our safety net.
 *
 * Usage in any component:
 *
 *   const showSchedule = useFeatureFlag("job_bookings_v2_phase_a");
 *   if (!showSchedule) return null;
 *
 * The first call kicks off a fetch of /api/feature-flags and caches the
 * resolved map for 60 seconds. Every other call to useFeatureFlag in
 * the same session is instant.
 *
 * When an admin flips a flag the response from PUT /api/admin/feature-flags/{key}
 * triggers `invalidateFeatureFlagCache()` so the next render reflects
 * the change without waiting for the cache to expire.
 *
 * Backend resolves admin-vs-not — the client just receives the final
 * boolean per flag. Never trust the client to claim admin-ness.
 */
import { useEffect, useState } from "react";
import api from "./api";

const CACHE_TTL_MS = 60 * 1000;

let cache = null;        // { flags: {key: bool}, fetchedAt: number }
let inflight = null;     // de-dupe concurrent first fetches
const listeners = new Set();

async function fetchFlags() {
  // If a fetch is already in flight, ride on it.
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await api.get("/feature-flags");
      cache = { flags: res.data?.flags || {}, fetchedAt: Date.now() };
      // Notify every subscribed hook to re-render.
      listeners.forEach((cb) => cb(cache.flags));
      return cache.flags;
    } catch {
      // On error, return whatever stale cache we have rather than
      // hiding features that should be visible.
      return (cache && cache.flags) || {};
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function invalidateFeatureFlagCache() {
  cache = null;
  fetchFlags(); // refetch immediately so flips reflect at once
}

/**
 * Returns true/false for a flag key. Initial render returns `false`
 * while the first fetch completes (worst case: a V2 feature hides for
 * ~100ms on first paint, which is preferable to leaking a hidden
 * feature to non-admins).
 */
export function useFeatureFlag(key) {
  const [enabled, setEnabled] = useState(() => {
    if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
      return !!cache.flags[key];
    }
    return false;
  });

  useEffect(() => {
    let alive = true;

    const apply = (flagsMap) => {
      if (alive) setEnabled(!!flagsMap[key]);
    };
    listeners.add(apply);

    // Fetch (or use cache) on mount.
    if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
      apply(cache.flags);
    } else {
      fetchFlags().then(apply);
    }

    return () => {
      alive = false;
      listeners.delete(apply);
    };
  }, [key]);

  return enabled;
}
